import { SpinSpeedType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { AssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { Model } from "@westpierstudio/luma/mvc";
import { IServiceProvider } from "@westpierstudio/luma/services";
import { cloneDeep } from "lodash";
import { OrientationSpec, ReelSetSymbols } from "servertypes/SharedTypes";

export enum ReelStopEase {
    LINEAR = `linear`,
    SINE_IN = `sineIn`,
    SINE_OUT = `sineOut`,
    SINE_IN_OUT = `sineInOut`,
    QUAD_IN = `quadIn`,
    QUAD_OUT = `quadOut`,
    QUAD_IN_OUT = `quadInOut`,
    CUBIC_IN = `cubicIn`,
    CUBIC_OUT = `cubicOut`,
    CUBIC_IN_OUT = `cubicInOut`,
    QUART_IN = `quartIn`,
    QUART_OUT = `quartOut`,
    QUART_IN_OUT = `quartInOut`,
    QUINT_IN = `quintIn`,
    QUINT_OUT = `quintOut`,
    QUINT_IN_OUT = `quintInOut`,
    CIRC_IN = `circIn`,
    CIRC_OUT = `circOut`,
    CIRC_IN_OUT = `circInOut`
};

interface SpinSettings {
    symbolsPerSecond: number,
    baseSpinTime: number,
    reelStartDelay: number,
    reelStopDelay: number,
    reelStopDistance: number,
    reelStopTimeIn: number,
    reelStopTimeOut: number,
    reelStopEaseIn: ReelStopEase,
    reelStopEaseOut: ReelStopEase,
    anticipationTime: number,
    anticipationSymbolsPerSec: number,
    anticipationStopTimeIn: number,
    anticipationStopTimeOut: number,
    instantSlam: boolean,
    logSpinTime: boolean
}

export interface SpinConfig extends SpinSettings {
    anticipationActive: boolean
    bonusHit: number
}

export interface ReelsSpec {
    position: OrientationSpec<AssetSpec>;
    scale: number,
    reelSpacing: number,
    symbolSize: { width: number, height: number },
    mask: {
        x: number,
        y: number,
        width: number,
        height: number
    }
    wildMask: {
        WX: AssetSpec
        WY: AssetSpec
    }
}

export interface ReelsLayout extends ReelsSpec {
    symbolPositions: { x: number, y: number }[][]
    positions: { x: number, y: number }[],
    scale: number,
    reelCount: number,
    symbolCount: number, //visible count
}

export enum SpinType {
    REGULAR = `regular`,
    FREE_SPIN = `freespin`,
    TURBO = `turbo`,
    TURBO_FREE_SPIN = `turbofreespin`
}

export class ReelsModel extends Model {
    private defaultSpinConfigs: Map<SpinType, SpinConfig> = new Map();
    private _activeSpinConfig!: SpinConfig;
    spinResult?: ReelSetSymbols;
    spinConfigs!: Map<SpinType, SpinConfig>;
    layout!: ReelsLayout;
    startTime = 0;

    constructor(services: IServiceProvider) {
        super(services);

        this.setupSpinConfigs();
    }

    get activeSpinConfig(): SpinConfig {
        return this._activeSpinConfig;
    }

    getSettingsForConfig(config: SpinConfig): SpinSettings {

        const settings: Record<SpinType, SpinSettings> = this.services.resources.getSetting(`reelSpinSettings`);
        const spinSettings = settings[SpinType.REGULAR];

        const out: Record<string, any> = {};

        for (const key of Object.keys(spinSettings)) {
            out[key] = config[key as keyof SpinSettings];
        }

        return out as SpinSettings;
    }

    getSpinType(freeSpin: boolean, spinSpeedType: SpinSpeedType): SpinType {

        if (spinSpeedType === SpinSpeedType.REGULAR) {
            return (freeSpin) ? SpinType.FREE_SPIN : SpinType.REGULAR;
        } else {
            return (freeSpin) ? SpinType.TURBO_FREE_SPIN : SpinType.TURBO;
        }
    }

    setupLayout(): ReelsLayout {

        const spec: ReelsSpec = cloneDeep(this.services.resources.getSpec(`reels`));

        const reelCount = 5;
        const symbolCount = 4;
        const positions = [];
        const symbolPositions = [];

        const { reelSpacing, symbolSize } = spec;

        for (let i = 0; i < reelCount; i++) {

            positions.push(
                {
                    x: (i - (reelCount - 1) / 2) * (symbolSize.width + reelSpacing),
                    y: -symbolSize.height * (symbolCount + 1) / 2
                }
            );

            const reelSymbolPos = [];

            for (let j = 0; j < symbolCount; j++) {

                reelSymbolPos.push(
                    {
                        x: positions[i].x,
                        y: positions[i].y + (symbolSize.height * (j + 1)) - symbolSize.height / 2.0
                    }
                );
            }

            symbolPositions.push(reelSymbolPos);
        }

        this.layout = {
            ...spec,
            symbolPositions,
            positions,
            reelCount,
            symbolCount
        };

        return this.layout;
    }

    setSpinConfigForSpinType(type: SpinType, anticipation: boolean): SpinConfig {

        this._activeSpinConfig = this.spinConfigs.get(type) as SpinConfig;
        this._activeSpinConfig.anticipationActive = anticipation && !this.activeSpinConfig.instantSlam;

        return this._activeSpinConfig;
    }

    updateSpinConfig(type: SpinType, config: SpinConfig): void {
        this.spinConfigs.set(type, config);
    }

    restoreDefaultSpinConfigs(): void {
        this.spinConfigs = cloneDeep(this.defaultSpinConfigs);
    }

    override reset(): void {

        this.spinResult = undefined;
        this.startTime = 0;
    }

    private setupSpinConfigs(): void {

        const settings: Record<SpinType, SpinSettings> = cloneDeep(this.services.resources.getSetting(`reelSpinSettings`));

        for (const [key, value] of Object.entries(settings)) {

            switch (key) {
                case SpinType.REGULAR:
                    this.defaultSpinConfigs.set(key, { ...value, anticipationActive: false, bonusHit: 0 });
                    break;
                case SpinType.FREE_SPIN:
                case SpinType.TURBO:
                case SpinType.TURBO_FREE_SPIN:
                    this.defaultSpinConfigs.set(key, this.makeCustomSpinConfig(settings[SpinType.REGULAR], value));
                    break;
            }
        }

        this.spinConfigs = cloneDeep(this.defaultSpinConfigs);
    }

    private makeCustomSpinConfig(defaultValues: Record<string, any>, customValues: Record<string, any>): SpinConfig {

        const customConfig = cloneDeep(defaultValues);

        for (const [key, value] of Object.entries(customValues)) {
            customConfig[key] = value;
        }

        customConfig.anticipationActive = false;
        customConfig.minSpinTimeActive = false;
        customConfig.minSpinTime = 0;

        return customConfig as SpinConfig;
    }
}